import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getLocais, createLocal, updateLocal, deleteLocal } from '../../api/locais';
import { getEmpresas } from '../../api/empresas';
import { useAuth } from '../../contexts/AuthContext';
import { TIPOS_LOCAL } from '../../utils/fitTestOpcoes';
import usePaginacao from '../../hooks/usePaginacao';
import Paginacao from '../../components/common/Paginacao';

const EMPTY = { nome: '', tipo: '', id_empresa: '', observacao: '' };

export default function Locais() {
  const { isFenix } = useAuth();
  const [locais, setLocais] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [busca, setBusca] = useState('');
  const [error, setError] = useState('');

  const load = () =>
    Promise.all([getLocais(), isFenix ? getEmpresas() : Promise.resolve([])])
      .then(([l, e]) => { setLocais(l); setEmpresas(e); })
      .finally(() => setLoading(false));

  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault(); setError('');
    try {
      await createLocal({ ...form, id_empresa: form.id_empresa || null });
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) { setError(err.response?.data?.detail || 'Erro ao cadastrar local'); }
  };

  const startEdit = (l) => {
    setEditId(l.id_local);
    setEditForm({ nome: l.nome, tipo: l.tipo || '', id_empresa: l.id_empresa || '', observacao: l.observacao || '' });
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setError('');
    try {
      await updateLocal(editId, { ...editForm, id_empresa: editForm.id_empresa || null });
      setEditId(null); load();
    } catch (err) { setError(err.response?.data?.detail || 'Erro ao atualizar local'); }
  };

  const handleDelete = async (local) => {
    if (!window.confirm(`Deseja excluir o local "${local.nome}"?`)) return;
    try { await deleteLocal(local.id_local); load(); }
    catch { setError('Não foi possível excluir. Verifique se há registros vinculados.'); }
  };

  const filtrados = locais.filter(l =>
    l.nome.toLowerCase().includes(busca.toLowerCase()) ||
    (l.tipo || '').toLowerCase().includes(busca.toLowerCase()) ||
    (l.empresa?.razao_social || '').toLowerCase().includes(busca.toLowerCase())
  );

  const pag = usePaginacao(filtrados);

  const empresaNome = (id) => {
    const e = empresas.find(e => e.id_empresa === id);
    return e ? (e.nome_fantasia || e.razao_social) : '—';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        title="Locais"
        subtitle="Locais, bases e unidades operacionais"
        action={
          isFenix && (
            <button className="btn-datafit" onClick={() => { setShowForm(!showForm); setEditId(null); }}>
              <i className="bi bi-plus me-2"></i>Novo local
            </button>
          )
        }
      />

      {error && (
        <div className="alert alert-danger py-2 small mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
          <button className="btn-close btn-sm float-end" onClick={() => setError('')}></button>
        </div>
      )}

      {showForm && (
        <div className="card-datafit p-4 mb-4">
          <div className="card-header-datafit mb-3" style={{ borderRadius: '0 12px 12px 0', display: 'inline-block' }}>
            Novo local
          </div>
          <form onSubmit={handleCreate}>
            <div className="row g-3">
              <div className="col-md-5">
                <div className="form-floating">
                  <input className="form-control" id="nome" placeholder="Nome" value={form.nome}
                    onChange={set('nome')} required />
                  <label htmlFor="nome">Nome *</label>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-floating">
                  <select className="form-select" id="tipo" value={form.tipo} onChange={set('tipo')}>
                    <option value="">Selecione...</option>
                    {TIPOS_LOCAL.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <label htmlFor="tipo">Tipo</label>
                </div>
              </div>
              {isFenix && (
                <div className="col-md-4">
                  <div className="form-floating">
                    <select className="form-select" id="empresa" value={form.id_empresa} onChange={set('id_empresa')}>
                      <option value="">Sem empresa vinculada</option>
                      {empresas.map(e => <option key={e.id_empresa} value={e.id_empresa}>{e.nome_fantasia || e.razao_social}</option>)}
                    </select>
                    <label htmlFor="empresa">Empresa</label>
                  </div>
                </div>
              )}
              <div className="col-12">
                <div className="form-floating">
                  <input className="form-control" id="obs" placeholder="Observação" value={form.observacao}
                    onChange={set('observacao')} />
                  <label htmlFor="obs">Observação</label>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button type="submit" className="btn-datafit" style={{ height: 40, fontSize: '.85rem' }}>Salvar</button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      <div className="card-datafit">
        <div className="p-3 border-bottom">
          <input type="text" className="form-control" style={{ maxWidth: 300 }}
            placeholder="Buscar por nome, tipo ou empresa..."
            value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <div className="table-responsive">
          <table className="table table-datafit mb-0">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                {isFenix && <th>Empresa</th>}
                <th>Observação</th>
                {isFenix && <th></th>}
              </tr>
            </thead>
            <tbody>
              {pag.paginados.length === 0 ? (
                <tr><td colSpan={isFenix ? 5 : 3} className="text-center text-muted py-4">Nenhum local encontrado</td></tr>
              ) : pag.paginados.map(l => (
                <React.Fragment key={l.id_local}>
                  <tr>
                    <td className="fw-500">{l.nome}</td>
                    <td>
                      {l.tipo
                        ? <span className="badge bg-secondary">{l.tipo}</span>
                        : <span className="text-muted">—</span>
                      }
                    </td>
                    {isFenix && <td className="small text-muted">{l.id_empresa ? empresaNome(l.id_empresa) : '—'}</td>}
                    <td className="small text-muted">{l.observacao || '—'}</td>
                    {isFenix && (
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-outline-primary" title="Editar"
                            onClick={() => editId === l.id_local ? setEditId(null) : startEdit(l)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" title="Excluir"
                            onClick={() => handleDelete(l)}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {editId === l.id_local && (
                    <tr key={`edit-${l.id_local}`} className="table-light">
                      <td colSpan={isFenix ? 5 : 3} className="p-3">
                        <form onSubmit={handleEdit} className="d-flex gap-2 flex-wrap align-items-end">
                          <div>
                            <label className="form-label small mb-1">Nome *</label>
                            <input className="form-control form-control-sm" style={{ width: 220 }}
                              value={editForm.nome} onChange={e => setEditForm({ ...editForm, nome: e.target.value })} required />
                          </div>
                          <div>
                            <label className="form-label small mb-1">Tipo</label>
                            <select className="form-select form-select-sm" style={{ width: 160 }}
                              value={editForm.tipo} onChange={e => setEditForm({ ...editForm, tipo: e.target.value })}>
                              <option value="">—</option>
                              {TIPOS_LOCAL.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          {isFenix && (
                            <div>
                              <label className="form-label small mb-1">Empresa</label>
                              <select className="form-select form-select-sm" style={{ width: 200 }}
                                value={editForm.id_empresa} onChange={e => setEditForm({ ...editForm, id_empresa: e.target.value })}>
                                <option value="">Sem empresa</option>
                                {empresas.map(e => <option key={e.id_empresa} value={e.id_empresa}>{e.nome_fantasia || e.razao_social}</option>)}
                              </select>
                            </div>
                          )}
                          <div>
                            <label className="form-label small mb-1">Observação</label>
                            <input className="form-control form-control-sm" style={{ width: 220 }}
                              value={editForm.observacao} onChange={e => setEditForm({ ...editForm, observacao: e.target.value })} />
                          </div>
                          <button type="submit" className="btn btn-sm btn-primary">Salvar</button>
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditId(null)}>Cancelar</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao {...pag} />
      </div>
    </>
  );
}
